import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Header } from "@/components/homepage/Header";
import { Hero } from "@/components/homepage/Hero";
import { SearchSection } from "@/components/homepage/SearchSection";
import { FeaturedOpportunity } from "@/components/homepage/FeaturedOpportunity";
import { LatestOpportunities } from "@/components/homepage/LatestOpportunities";
import { Industries } from "@/components/homepage/Industries";
import { Countries } from "@/components/homepage/Countries";
import { Footer } from "@/components/homepage/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { BottomNav } from "@/components/BottomNav";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { QuickFilters } from "@/components/QuickFilters";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatusBanner } from "@/components/NetworkStatusBanner";
import { Toaster } from "@/components/ui/sonner";
import OpportunitiesListPage from "@/pages/OpportunitiesListPage";
import OpportunityDetailPage from "@/pages/OpportunityDetailPage";
import AboutPage from "@/pages/AboutPage";
import KnowledgeLibraryPage from "@/pages/KnowledgeLibraryPage";
import KnowledgeStoryPage from "@/pages/KnowledgeStoryPage";
import SavedPage from "@/pages/SavedPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { SignInPage, SignUpPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "@/pages/account/AuthPages";
import { ProfilePage, ChangePasswordPage, OnboardingPage } from "@/pages/account/AccountPages";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import OpportunityReviewPage from "@/pages/admin/OpportunityReviewPage";
import OpportunityEditorPage from "@/pages/admin/OpportunityEditorPage";
import SourcesPage from "@/pages/admin/SourcesPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminContentPage from "@/pages/admin/AdminContentPage";
import AdminAppearancePage from "@/pages/admin/AdminAppearancePage";
import AdminCoveragePage from "@/pages/admin/AdminCoveragePage";
import SignalsArchivePage from "@/pages/SignalsArchivePage";
import SignalDetailPage from "@/pages/SignalDetailPage";
import FeedMonitorPage from "@/pages/admin/FeedMonitorPage";
import AIQueuePage from "@/pages/admin/AIQueuePage";
import AdminKnowledgeListPage from "@/pages/admin/AdminKnowledgeListPage";
import AdminKnowledgeEditorPage from "@/pages/admin/AdminKnowledgeEditorPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";
import { MarketProvider } from "@/context/MarketContext";
import { DashboardStrip } from "@/components/homepage/DashboardStrip";
import { MarketInsights } from "@/components/homepage/MarketInsights";
import { OpportunitiesProvider, useOpportunities } from "@/hooks/useOpportunities";

const HomeContent = () => {
  const { featured } = useOpportunities();
  return (
    <div className="min-h-screen bg-surface-50 text-ink-900">
      <Header />
      <main>
        {/* 1 · Hero */}
        <Hero />
        {/* 2 · Global Search */}
        <SearchSection />
        {/* 3 · Quick Filters */}
        <QuickFilters />
        {/* 4 · Featured Opportunity */}
        <FeaturedOpportunity opportunity={featured} />
        {/* 5 · Latest Opportunities */}
        <LatestOpportunities />
        {/* 6 · Trending Sectors */}
        <Industries />
        {/* 7 · Top Countries */}
        <Countries />
        {/* 8 · Investment Dashboard */}
        <DashboardStrip />
        {/* 9 · Market Insights */}
        <MarketInsights />
        {/* 10 · Footer */}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <NetworkStatusBanner />
        <AnnouncementBanner />
        <AuthProvider>
          <MarketProvider>
          <OpportunitiesProvider>
            <Routes>
              <Route path="/" element={<HomeContent />} />
              <Route path="/opportunities" element={<OpportunitiesListPage />} />
              <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
              <Route path="/signals" element={<SignalsArchivePage />} />
              <Route path="/signals/:id" element={<SignalDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              {/* Sprint 20: /lessons retired — all educational content
                  lives under the unified Knowledge Library. Preserve
                  inbound links with a permanent redirect. */}
              <Route path="/lessons" element={<Navigate to="/knowledge" replace />} />
              <Route path="/lessons/:slug" element={<Navigate to="/knowledge" replace />} />
              <Route path="/knowledge" element={<KnowledgeLibraryPage />} />
              <Route path="/knowledge/:id" element={<KnowledgeStoryPage />} />
              <Route path="/saved" element={<SavedPage />} />

              {/* Sprint 28a — legal + contact.
                  Replaces the deleted /settings page and the three "coming
                  soon" toasts. Every visible control now has a real page. */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms"   element={<TermsPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Sprint 28b — public user account. */}
              <Route path="/account/sign-in"          element={<SignInPage />} />
              <Route path="/account/sign-up"          element={<SignUpPage />} />
              <Route path="/account/forgot-password"  element={<ForgotPasswordPage />} />
              <Route path="/account/reset-password"   element={<ResetPasswordPage />} />
              <Route path="/account/verify"           element={<VerifyEmailPage />} />
              <Route path="/profile"                  element={<ProfilePage />} />
              <Route path="/profile/change-password"  element={<ChangePasswordPage />} />
              <Route path="/onboarding"               element={<OnboardingPage />} />

              {/* Old /settings was a shallow "about the app" surface with
                  three placeholder rows. Redirect to /about (its only real
                  content — Saved is already in primary nav). */}
              <Route path="/settings" element={<Navigate to="/about" replace />} />

              {/* Sprint 25 — Admin auth. /admin/login is public; every
                  other /admin/* route is gated by ProtectedRoute with
                  the admin+editor role set. Individual admin-only
                  actions (e.g. sources CRUD) are enforced server-side. */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route element={<ProtectedRoute roles={["admin", "editor"]} />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/review" element={<OpportunityReviewPage />} />
                <Route path="/admin/opportunities/:id/edit" element={<OpportunityEditorPage />} />
                <Route path="/admin/feed" element={<FeedMonitorPage />} />
                <Route path="/admin/ai-queue" element={<AIQueuePage />} />
                <Route path="/admin/sources" element={<SourcesPage />} />
                <Route path="/admin/knowledge" element={<AdminKnowledgeListPage />} />
                <Route path="/admin/knowledge/new" element={<AdminKnowledgeEditorPage />} />
                <Route path="/admin/knowledge/:id" element={<AdminKnowledgeEditorPage />} />
                {/* /admin/settings — sidebar link hidden in Sprint 28a until
                    the full Brand Management sprint (Sprint B) lands.
                    Deep-link support is preserved to avoid breaking bookmarks. */}
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="/admin/content" element={<AdminContentPage />} />
                <Route path="/admin/appearance" element={<AdminAppearancePage />} />
                <Route path="/admin/coverage" element={<AdminCoveragePage />} />
              </Route>

              {/* Sprint 28a — 404 catch-all. Any unknown route lands here
                  instead of rendering blank content under the header. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <BottomNav />
          </OpportunitiesProvider>
          </MarketProvider>
        </AuthProvider>
        <Toaster position="top-center" richColors closeButton dir="rtl" />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
